import React from 'react';
import { Button, Box, Collapse } from '@mui/material';
import type { GeoJsonFeature } from '../utils/ogc';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
// import { DatePicker } from '@mantine/dates';
// import { addDays, subDays, addMonths, addYears, subMonths, subYears } from 'date-fns';

export default function StreetView({ feature }: { feature: GeoJsonFeature }) {
    const [open, setOpen] = React.useState(false);
    const coords = (feature?.geometry?.coordinates || []) as number[];
    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    const mapsUrl = Number.isFinite(lat) && Number.isFinite(lng) ? `https://www.google.com/maps?q=${lat},${lng}` : undefined;
    return (
      <>
        <Button variant="contained" size="small" onClick={() => setOpen(!open)} sx={{ mt: 2, width: '100%' }}>
          Street View
        </Button>
        {mapsUrl && (
          <Button
            variant="text"
            size="small"
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 0.5, width: '100%' }}
          >
            Open in Google Maps
          </Button>
        )}
        <Collapse in={open}>
          <Box sx={{ mt: 2 }}>
            <StreetViewBlock feature={feature} />
          </Box>
        </Collapse>
      </>
    );
}

export function StreetViewBlock({ feature, height=600 }: { feature: GeoJsonFeature, height?: number }) {
  const streetViewLib = useMapsLibrary('streetView');
  const geometryLib = useMapsLibrary('geometry');
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const panoRef = React.useRef<google.maps.StreetViewPanorama | null>(null);
  const markerRef = React.useRef<google.maps.Marker | null>(null);
  const [noPano, setNoPano] = React.useState(false);
  const coords = (feature?.geometry?.coordinates || []) as number[];
  const lng = Number(coords[0]);
  const lat = Number(coords[1]);

  React.useEffect(() => {
    if (!containerRef.current || !streetViewLib) return;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setNoPano(false);

    // Find nearest panorama to the sensor, then aim camera at the sensor location.
    const sv = new google.maps.StreetViewService();
    sv.getPanorama({ location: { lat, lng }, radius: 75 }, (data, status) => {
      if (status !== google.maps.StreetViewStatus.OK || !data || !data.location) {
        setNoPano(true);
        return;
      }
      const panoId = data.location.pano;
      const panoLatLng = data.location.latLng;
      let heading = 0;
      try {
        if (geometryLib && panoLatLng) {
          heading = google.maps.geometry.spherical.computeHeading(panoLatLng, { lat, lng });
        }
      } catch {}

      const pov: google.maps.StreetViewPov = { heading, pitch: -10 /* ~2m eye height, slight downward tilt */ };
      if (!panoRef.current) {
        panoRef.current = new google.maps.StreetViewPanorama(containerRef.current!, {
          pano: panoId,
          pov,
          visible: true,
          addressControl: false,
          motionTracking: false,
          clickToGo: true,
          linksControl: true,
          fullscreenControl: true,
        });
      } else {
        panoRef.current.setPano(panoId);
        panoRef.current.setPov(pov);
      }

      // Add/update a marker at the sensor location inside the panorama
      try {
        if (markerRef.current) {
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
        markerRef.current = new google.maps.Marker({
          position: { lat, lng },
          // @ts-expect-error StreetViewPanorama is a valid Map for Marker
          map: panoRef.current!,
          title: feature?.properties?.name || 'Sensor',
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            fillColor: '#0e9089',
            fillOpacity: 1,
          },
        });
      } catch {}
    });
  }, [streetViewLib, geometryLib, feature, lat, lng]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return (
    <Box>
      {noPano ? (
        <Box sx={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1, border: '1px dashed', borderColor: 'divider', color: 'text.secondary' }}>
          No Street View coverage at this location
        </Box>
      ) : (
        <Box ref={containerRef} sx={{ width: '100%', height, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} />
      )}
    </Box>
  );
}
